<script setup lang="ts">
import { ref, computed } from 'vue'
import UiButton from '@/components/ui/button/Button.vue'
import SimpleCard from '@/components/SimpleCard.vue'
import RandomSvg from '@/assets/icons/random.svg'
import type { Card } from '@acepoker/contract/action'

const suitList = ['diamonds', 'spades', 'hearts', 'clubs'] as Card['suit'][]
const rankList = [
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  'T',
  'J',
  'Q',
  'K',
  'A'
].reverse() as Card['rank'][]

const {
  cards = [],
  count = 1,
  disabledCards = [],
  tableCards = []
} = defineProps<{
  cards: (Card | null)[]
  count?: number
  tableCards?: (Card | null)[]
  disabledCards?: Card[]
}>()

const emit = defineEmits<{
  change: [data: (Card | null)[]]
}>()

const selectedCards = ref(cards.length ? [...cards] : Array(count).fill(null))

const allDisabledCards = computed<string[]>(() => [
  ...tableCards.filter(Boolean).map(cardToStr),
  ...disabledCards.map(cardToStr)
])
const currSelectedCards = computed<string[]>(() => [
  ...(selectedCards.value ?? []).filter(Boolean).map(cardToStr)
])

const selectIndex = ref(0)

function handleCardClick(card: Card | null) {
  const cardIndex = card ? selectedCards.value.findIndex((el) => isSameCard(el, card)) : -1

  if (cardIndex > -1) {
    selectedCards.value[cardIndex] = null
    selectIndex.value = cardIndex
    return
  }

  selectedCards.value[selectIndex.value] = card

  if (selectIndex.value < count - 1) {
    selectIndex.value++
  }
}

function handleSave() {
  emit('change', [...selectedCards.value])
}

function handleClear() {
  selectedCards.value = Array(count).fill(null)
  selectIndex.value = 0
}

// helper
function cardToStr(card: Card): string {
  return card.rank + card.suit[0]
}
function isSameCard(a: Card, b: Card): boolean {
  return a?.rank === b?.rank && a?.suit[0] === b?.suit[0]
}
</script>

<template>
  <section>
    <div class="mb-3 flex justify-center gap-1 align-middle">
      <!-- cards on table -->
      <simple-card
        v-for="(card, i) in tableCards"
        :key="i"
        class="selected-card shrink-0 aria-disabled:pointer-events-none aria-disabled:opacity-50"
        size="lg"
        aria-disabled="true"
        :value="card ? cardToStr(card) : null"
        :random="!card"
      />

      <!-- current selection -->
      <simple-card
        v-for="(card, i) in selectedCards"
        :key="i"
        class="selected-card shrink-0"
        size="lg"
        :value="card ? cardToStr(card) : null"
        :random="!card"
        :selected="i === selectIndex"
        @click="selectIndex = i"
      />
    </div>

    <p class="mb-3 text-center">{{ $t('NewDrill.label.TapCardToEdit') }}</p>

    <div class="mb-3 flex flex-col items-center justify-center">
      <div v-for="suit of suitList" class="mt-0.5 flex gap-0.5" :key="suit">
        <simple-card
          class="shrink-0 aria-disabled:pointer-events-none aria-disabled:opacity-30 aria-selected:opacity-70"
          v-for="rank of rankList"
          :key="`${rank}${suit}`"
          :value="cardToStr({ rank, suit })"
          :aria-disabled="allDisabledCards.indexOf(cardToStr({ rank, suit })) > -1"
          :aria-selected="currSelectedCards.indexOf(cardToStr({ rank, suit })) > -1"
          @click="handleCardClick({ rank, suit })"
        />
      </div>
    </div>

    <div class="mb-3 flex justify-center text-center">
      <ui-button variant="secondary2" type="button" @click="handleCardClick(null)">
        <RandomSvg class="mr-2" />
        {{ $t('NewDrill.button.KeepRandom') }}
      </ui-button>
    </div>

    <div class="mt-6 flex justify-center gap-2 align-middle">
      <ui-button
        class="w-[50%] max-w-44"
        type="button"
        variant="default2"
        size="lg"
        @click.stop="handleSave"
        >{{ $t('NewDrill.button.Update') }}</ui-button
      >
      <ui-button
        class="w-[50%] max-w-44"
        type="button"
        variant="secondary2"
        size="lg"
        @click.stop="handleClear"
        >{{ $t('NewDrill.button.Clear') }}</ui-button
      >
    </div>
  </section>
</template>

<style scoped>
.selected-card:nth-child(3),
.selected-card:nth-child(4) {
  margin-right: 14px;
}
</style>
